/**
 * DeliveryVerificationTab — Onglet "Vérification" de la page Réception POS
 *
 * Refonte mars 2026 : flux checklist de réception
 * - Approuver une ligne = créer directement le produit + lot en réserve
 * - Séparation En attente (cartes détaillées) / Réceptionné (liste compacte)
 * - Barre de progression
 * - Édition inline (tap pour éditer un champ)
 * - Scan EAN = auto-focus sur la ligne + champ DLC
 */
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import api, {
  Delivery,
  DeliveryLine,
  ProductSuggestion,
} from '../../services/api';
import { useInvitationNouveaute } from './InvitationNouveaute';
import { useTenant } from '../../contexts/TenantContext';
import ProductAutocomplete from '../ProductAutocomplete';
import { suggestSupplier } from '../../utils/supplierMatch';
import DLCInput from '../ui/DLCInput';
import PriceInput from '../ui/PriceInput';
import WebcamCaptureModal from '../ui/WebcamCaptureModal';
import ArticleEnPlusModal from './ArticleEnPlusModal';
import BarcodeScannerModal from '../ui/BarcodeScannerModal';
import { extractLabelLocal } from '../../services/camera/localRecognition';
import { prepareImageForUpload, downscaleForVision } from '../../utils/imagePrep';

interface Category {
  id: string;
  name: string;
  icon?: string | null;
}

const REJECT_REASONS = [
  'Produit endommagé',
  'Mauvaise qualité',
  'Quantité incorrecte',
  'DLC trop courte',
  'Produit manquant',
];

// Prix de vente conseillé : prix d'achat TTC × 1,5, arrondi au 0,05 € SUPÉRIEUR
// (ex. 1,51 € → 1,55 €). Centralise le calcul (init + conditionnement).
const suggestedSalePriceCents = (purchaseTtcCents: number): number =>
  Math.ceil((purchaseTtcCents * 1.5) / 5) * 5;

interface DeliveryVerificationTabProps {
  deliveryId: string | null;
  onDeliveryChange: (id: string | null) => void;
  scannedBarcode: string | null;
  onBarcodeClear: () => void;
  onDeliveryApplied: () => void;
  onPendingCountChange: (count: number) => void;
}

export default function DeliveryVerificationTab({
  deliveryId,
  onDeliveryChange,
  scannedBarcode,
  onBarcodeClear,
  onDeliveryApplied,
  onPendingCountChange,
}: DeliveryVerificationTabProps) {
  const { tenant } = useTenant();

  // Liste des livraisons (mode sélection)
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  // Détail livraison (mode vérification)
  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const [lines, setLines] = useState<DeliveryLine[]>([]);
  const [loading, setLoading] = useState(false);

  // Édition fournisseur
  const [isEditingSupplier, setIsEditingSupplier] = useState(false);
  const [supplierName, setSupplierName] = useState('');
  const [suppliers, setSuppliers] = useState<string[]>([]); // liste dédupliquée (combobox)
  // Suggestion « fournisseur proche existant » (fautes de frappe) — sans fusion auto.
  const supplierSuggestion = useMemo(
    () => suggestSupplier(supplierName, suppliers),
    [supplierName, suppliers],
  );
  // Suggestion PROACTIVE sur le fournisseur déjà enregistré — couvre les fichiers
  // uploadés (pas de saisie à l'upload : l'IA extrait le nom, un typo passerait sinon
  // inaperçu). Affichée directement à côté du nom, sans avoir à ouvrir l'édition.
  const deliverySupplierSuggestion = useMemo(
    () => suggestSupplier(delivery?.supplier || '', suppliers),
    [delivery?.supplier, suppliers],
  );

  // Catégories
  const [categories, setCategories] = useState<Category[]>([]);

  // Regex UUID pour détecter si category_hint est un ID ou un nom
  const isUuid = (s: string): boolean =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

  // Résout un category_hint (UUID ou nom) vers un libellé affichable
  const resolveCategoryLabel = (hint: string | null | undefined): string | null => {
    if (!hint) return null;
    if (isUuid(hint)) {
      return categories.find(c => c.id === hint)?.name ?? null;
    }
    return hint;
  };

  // DLC inline pour les lignes
  const [lineDlcValues, setLineDlcValues] = useState<Record<string, string>>({});

  // Ligne en cours d'édition inline (null = aucune)
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editedFields, setEditedFields] = useState<Record<string, any>>({});

  // Rejet
  const [rejectingLineId, setRejectingLineId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [customRejectReason, setCustomRejectReason] = useState('');

  // Prix de vente conseillé par ligne (local, pas persisté dans DeliveryLine)
  const [lineSuggestedPrices, setLineSuggestedPrices] = useState<Record<string, number>>({});
  // Lignes dont le prix de vente a été modifié manuellement (≠ suggestion auto)
  const [linesPriceEdited, setLinesPriceEdited] = useState<Set<string>>(new Set());
  // Lignes où l'opérateur a cliqué « Appliquer le prix proposé » (produit existant) →
  // applique le prix proposé à la fiche + POS en un clic, sans confirmation
  const [linesApplyProposed, setLinesApplyProposed] = useState<Set<string>>(new Set());

  // Ligne en cours de traitement (spinner)
  const [processingLineId, setProcessingLineId] = useState<string | null>(null);
  // Lignes pour lesquelles l'utilisateur a explicitement cliqué "Nouveau" — empêche l'auto-association par code-barres au backend
  const [linesMarkedAsNew, setLinesMarkedAsNew] = useState<Set<string>>(new Set());

  // Section réceptionné repliée
  const [showProcessed, setShowProcessed] = useState(false);

  // Recherche
  const [searchQuery, setSearchQuery] = useState('');

  // Scan highlight
  const [highlightedLineId, setHighlightedLineId] = useState<string | null>(null);
  const lineRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dlcRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Ligne SÉLECTIONNÉE pour le scan : l'opérateur touche une ligne, puis scanne
  // l'article → le matching produit s'applique à CETTE ligne (zéro erreur de cible).
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const selectedLineIdRef = useRef<string | null>(null);
  selectedLineIdRef.current = selectedLineId;
  // Candidats EAN partagé (cas café moulu/grain) à désambiguïser
  const [scanCandidates, setScanCandidates] = useState<{ lineId: string; candidates: any[] } | null>(null);
  // EAN scanné inconnu de la livraison : choix explicite de la ligne cible
  const [scanLineChooser, setScanLineChooser] = useState<{ barcode: string } | null>(null);

  // Scan code-barres caméra
  const [scannerOpen, setScannerOpen] = useState(false);

  // Charger les livraisons (mode sélection)
  const loadDeliveryList = useCallback(async () => {
    if (!tenant?.id) return;
    try {
      setLoadingList(true);
      const data = await api.listDeliveries(tenant.id, { limit: 50 });
      const pendingOrApproved = data.filter((d: any) => d.status !== 'applied');
      setDeliveries(pendingOrApproved);
      onPendingCountChange(pendingOrApproved.filter((d: any) => d.status === 'pending').length);
    } catch (err) {
      console.error('Failed to load deliveries:', err);
    } finally {
      setLoadingList(false);
    }
  }, [tenant?.id]);

  useEffect(() => {
    if (!deliveryId) loadDeliveryList();
  }, [deliveryId, loadDeliveryList]);

  // Charger la liste dédupliquée des fournisseurs (combobox d'édition)
  useEffect(() => {
    if (!tenant?.id) return;
    api.getSuppliers(tenant.id).then(setSuppliers).catch(() => { /* best-effort */ });
  }, [tenant?.id]);

  // Charger le détail d'une livraison
  // « + Article reçu en plus » : un article livré absent du document importé
  const [articleEnPlusOuvert, setArticleEnPlusOuvert] = useState(false);

  const loadDeliveryDetails = useCallback(async () => {
    if (!deliveryId || !tenant?.id) return;
    try {
      setLoading(true);
      const [deliveryData, linesData, cats] = await Promise.all([
        api.getDelivery(tenant.id, deliveryId),
        api.getDeliveryLines(tenant.id, deliveryId),
        api.getCategories(),
      ]);
      setDelivery(deliveryData);
      setLines(linesData);
      setSupplierName(deliveryData.supplier || '');
      // Initialiser les producteurs par ligne
      const producers: Record<string, string> = {};
      linesData.forEach((l: any) => {
        if (l.producer_name) producers[l.id] = l.producer_name;
      });
      setLineProducers(prev => ({ ...prev, ...producers }));

      // Auto-charger les suggestions pour les lignes pending
      const pending = linesData.filter((l: any) => l.status === 'pending');
      for (const l of pending) {
        loadSuggestions(l.id, l.producer_name || undefined);
      }
      setCategories(cats);

      // Initialiser DLC values
      const dlcMap: Record<string, string> = {};
      linesData.forEach((line: any) => {
        if (line.default_expiry_date) {
          try {
            dlcMap[line.id] = new Date(line.default_expiry_date).toISOString().split('T')[0];
          } catch { /* invalid date */ }
        }
      });
      setLineDlcValues(dlcMap);

      // Initialiser les prix de vente conseillés (achat TTC × 1,5, arrondi au 0,05 € sup.)
      // Ne pas écraser les prix déjà modifiés manuellement
      setLineSuggestedPrices(prev => {
        const merged = { ...prev };
        linesData.forEach((line: any) => {
          if (line.unit_price_ttc_cents && !merged[line.id]) {
            merged[line.id] = suggestedSalePriceCents(line.unit_price_ttc_cents);
          }
        });
        return merged;
      });
    } catch (err: any) {
      console.error('Failed to load delivery:', err);
      alert('Erreur de chargement de la livraison');
      onDeliveryChange(null);
    } finally {
      setLoading(false);
    }
  }, [deliveryId, tenant?.id]);

  useEffect(() => {
    if (deliveryId) loadDeliveryDetails();
  }, [deliveryId, loadDeliveryDetails]);

  const isOrderDelivery = delivery?.document_type === 'ORDER';

  // État pour l'upload BL PDF
  const [attachingBL, setAttachingBL] = useState(false);
  const blFileInputRef = useRef<HTMLInputElement>(null);
  // État pour l'upload photo étiquette
  const [uploadingPhotoLineId, setUploadingPhotoLineId] = useState<string | null>(null);
  const labelPhotoInputRef = useRef<HTMLInputElement>(null);
  const [labelPhotoTargetLineId, setLabelPhotoTargetLineId] = useState<string | null>(null);
  const [webcamLineId, setWebcamLineId] = useState<string | null>(null);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  // Extraction DLC/lot depuis la photo d'étiquette (Claude Vision)
  const [extractingLabelLineId, setExtractingLabelLineId] = useState<string | null>(null);
  const [labelExtract, setLabelExtract] = useState<Record<string, { dlc: string | null; lot_number: string | null; date_type: string | null; confidence: number }>>({});
  // Code-barres par ligne (saisie locale avant persistance)
  const [lineBarcodes, setLineBarcodes] = useState<Record<string, string>>({});

  // Barcode scan → auto-focus sur la ligne + champ DLC (ou réception ORDER)
  useEffect(() => {
    if (!scannedBarcode || lines.length === 0) return;

    // PRIORITÉ : si une ligne est sélectionnée, le scan s'y applique directement
    // (geste « sélectionner la ligne puis scanner »). Le matching produit par EAN
    // s'exécute à cet instant — c'est le point clé anti-doublon.
    const targetLineId = selectedLineIdRef.current;
    if (targetLineId && lines.some(l => l.id === targetLineId && l.status === 'pending')) {
      applyScanToLine(targetLineId, scannedBarcode);
      onBarcodeClear();
      return;
    }

    // Pour les commandes ORDER, utiliser l'endpoint de réception par code-barres
    if (isOrderDelivery && deliveryId) {
      (async () => {
        try {
          const result = await api.receiveLineByBarcode(deliveryId, scannedBarcode);
          // Mettre à jour la ligne localement
          setLines(prev => prev.map(l =>
            l.id === result.id
              ? { ...l, reception_status: 'received' as const, received_quantity: result.received_quantity ?? l.quantity }
              : l
          ));
          setHighlightedLineId(result.id);
          setTimeout(() => {
            lineRefs.current[result.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
          setTimeout(() => setHighlightedLineId(null), 3000);
        } catch {
          alert(`Code "${scannedBarcode}" non trouve dans cette commande`);
        }
      })();
      onBarcodeClear();
      return;
    }

    // SCAN DIRECT (sans sélection préalable) — même moteur EAN-prioritaire que le
    // scan ciblé. La ligne cible est retrouvée par l'EAN, jamais choisie arbitrairement.

    // 1) Une ligne en attente porte déjà cet EAN (saisi, produit matché, ou code
    //    fournisseur) → c'est elle : matching produit par EAN immédiat.
    const pendingWithEan = lines.find(l =>
      l.status === 'pending' && (
        l.barcode === scannedBarcode ||
        l.matched_product_barcode === scannedBarcode ||
        l.product_code === scannedBarcode
      )
    );
    if (pendingWithEan) {
      applyScanToLine(pendingWithEan.id, scannedBarcode);
      onBarcodeClear();
      return;
    }

    // 2) EAN porté par une ligne déjà traitée → surligner pour information, sans rien modifier.
    const processedWithEan = lines.find(l =>
      l.barcode === scannedBarcode ||
      l.matched_product_barcode === scannedBarcode ||
      l.product_code === scannedBarcode
    );
    if (processedWithEan) {
      setHighlightedLineId(processedWithEan.id);
      setTimeout(() => {
        lineRefs.current[processedWithEan.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      setTimeout(() => setHighlightedLineId(null), 3000);
      onBarcodeClear();
      return;
    }

    // 3) EAN inconnu des lignes : s'il ne reste qu'UNE ligne en attente sans
    //    code-barres, aucune ambiguïté → on lui applique le scan. Sinon, choix
    //    explicite de la ligne par l'opérateur (zéro affectation arbitraire).
    const pendingLines = lines.filter(l => l.status === 'pending');
    const pendingWithoutBarcode = pendingLines.filter(l => !l.barcode && !l.matched_product_barcode);
    if (pendingWithoutBarcode.length === 1) {
      applyScanToLine(pendingWithoutBarcode[0].id, scannedBarcode);
    } else if (pendingLines.length > 0) {
      setScanLineChooser({ barcode: scannedBarcode });
    } else {
      alert(`Code "${scannedBarcode}" non trouve dans cette livraison`);
    }

    onBarcodeClear();
  }, [scannedBarcode]);

  // Attacher BL PDF
  const handleAttachBL = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !deliveryId) return;
    e.target.value = '';
    setAttachingBL(true);
    try {
      const result = await api.attachDocument(deliveryId, file);
      setDelivery(prev => prev ? {
        ...prev,
        document_url: result.document_url,
        document_filename: result.document_filename,
      } : null);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erreur lors de l\'upload');
    } finally {
      setAttachingBL(false);
    }
  };

  // Marquer une ligne comme reçue manuellement (ORDER)
  const handleReceiveLine = async (lineId: string, receivedQty: number) => {
    try {
      const result = await api.receiveLine(lineId, {
        received_quantity: receivedQty,
        reception_status: 'received',
      });
      setLines(prev => prev.map(l =>
        l.id === lineId
          ? { ...l, reception_status: result.reception_status as any, received_quantity: result.received_quantity }
          : l
      ));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erreur');
    }
  };


  // Lire la DLC + n° de lot depuis la photo (Claude Vision) et pré-remplir le champ DLC.
  // Pré-remplissage à confirmer par l'opérateur (les étiquettes varient trop).
  const runLabelExtraction = async (lineId: string, file: File) => {
    setExtractingLabelLineId(lineId);
    try {
      const base64 = await downscaleForVision(file);
      if (!base64) return;
      // 1) Lecture LOCALE via le bridge (Apple Vision OCR). Gratuite, ~instantanée.
      let info = await extractLabelLocal(base64);
      // 2) Repli CLOUD si le bridge est injoignable. Indispensable sur iPad et
      //    iPhone, qui n'ont pas de bridge : sans ce repli la lecture de DLC ne
      //    pouvait JAMAIS marcher sur ces appareils, sans message d'erreur.
      //    Coût ~0,002 €/photo, donc réservé au cas où le local a échoué.
      if (!info) {
        try {
          // downscaleForVision ré-encode toujours en JPEG via canvas.
          const cloud = await api.extractLabelInfo(base64, 'image/jpeg');
          info = cloud ? { ...cloud, source: 'cloud' as const } : null;
        } catch {
          // Best-effort des deux côtés : un échec ne bloque jamais la réception.
        }
      }
      if (!info) return;
      setLabelExtract(prev => ({ ...prev, [lineId]: info }));
      // Ne pré-remplir la DLC que si rien n'a été saisi à la main pour cette ligne
      if (info.dlc) {
        setLineDlcValues(prev => (prev[lineId] ? prev : { ...prev, [lineId]: info.dlc as string }));
      }
    } catch {
      // Extraction best-effort : un échec ne bloque jamais la réception
    } finally {
      setExtractingLabelLineId(null);
    }
  };

  // Upload photo étiquette pour une ligne (fichier / caméra native smartphone)
  const handleLabelPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !labelPhotoTargetLineId) return;
    const lineId = labelPhotoTargetLineId;
    e.target.value = '';
    setUploadingPhotoLineId(lineId);
    try {
      const toUpload = await prepareImageForUpload(file);
      const result = await api.uploadLineLabelPhoto(lineId, toUpload);
      setLines(prev => prev.map(l =>
        l.id === lineId
          ? { ...l, label_photo_url: result.label_photo_url }
          : l
      ));
      void runLabelExtraction(lineId, toUpload);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erreur upload photo');
    } finally {
      setUploadingPhotoLineId(null);
      setLabelPhotoTargetLineId(null);
    }
  };

  // Upload photo étiquette depuis webcam
  const handleWebcamCapture = async (file: File) => {
    if (!webcamLineId) return;
    const lineId = webcamLineId;
    setUploadingPhotoLineId(lineId);
    try {
      const result = await api.uploadLineLabelPhoto(lineId, file);
      setLines(prev => prev.map(l =>
        l.id === lineId
          ? { ...l, label_photo_url: result.label_photo_url }
          : l
      ));
      void runLabelExtraction(lineId, file);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erreur upload photo');
    } finally {
      setUploadingPhotoLineId(null);
      setWebcamLineId(null);
    }
  };

  // Suggestions produit par ligne (line.id → ProductSuggestion[])
  const [lineSuggestions, setLineSuggestions] = useState<Record<string, ProductSuggestion[]>>({});
  const [loadingSuggestionsFor, setLoadingSuggestionsFor] = useState<Set<string>>(new Set());
  // Producteur par ligne
  const [lineProducers, setLineProducers] = useState<Record<string, string>>({});
  const [editingProducerLineId, setEditingProducerLineId] = useState<string | null>(null);

  // Sauvegarder le fournisseur
  const handleSaveSupplier = async () => {
    if (!deliveryId || !tenant?.id || !supplierName.trim()) return;
    try {
      await api.updateDelivery(tenant.id, deliveryId, { supplier: supplierName });
      setDelivery(prev => prev ? { ...prev, supplier: supplierName } : null);
      setIsEditingSupplier(false);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Échec de la mise à jour');
    }
  };

  // Charger les suggestions pour une ligne
  const loadSuggestions = async (lineId: string, producerName?: string) => {
    setLoadingSuggestionsFor(prev => new Set(prev).add(lineId));
    try {
      const result = await api.getLineProductMatches(lineId, producerName);
      setLineSuggestions(prev => ({ ...prev, [lineId]: result.matches }));
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    } finally {
      setLoadingSuggestionsFor(prev => {
        const next = new Set(prev);
        next.delete(lineId);
        return next;
      });
    }
  };

  // Scan EAN appliqué à une ligne (sélectionnée OU retrouvée par l'EAN) :
  // le matching produit s'exécute ici. Code-barres prioritaire → lie le
  // produit existant (doublon impossible).
  const applyScanToLine = async (lineId: string, barcode: string) => {
    if (!tenant?.id || !deliveryId) return;
    try {
      const updated: any = await api.scanLineBarcode(deliveryId, lineId, barcode);
      // Refléter immédiatement la ligne enrichie (match vert si produit lié)
      setLines(prev => prev.map(l => l.id === lineId ? { ...l, ...updated } : l));

      if (updated.scan_outcome === 'matched') {
        // Produit existant lié → annuler une éventuelle intention "Nouveau"
        setLinesMarkedAsNew(prev => { const n = new Set(prev); n.delete(lineId); return n; });
        setScanCandidates(null);
        loadSuggestions(lineId, lineProducers[lineId] || undefined);
        // Enchaîner sur la saisie DLC (prochaine action naturelle après le scan)
        setTimeout(() => dlcRefs.current[lineId]?.focus(), 400);
      } else if (updated.scan_outcome === 'multiple') {
        // EAN partagé entre plusieurs produits distincts (ex. café moulu/grain) :
        // l'opérateur doit choisir — on n'a RIEN lié automatiquement.
        setScanCandidates({ lineId, candidates: updated.scan_candidates || [] });
      } else {
        // Aucun produit avec cet EAN : l'EAN est enregistré sur la ligne. Les
        // similaires éventuels seront re-confirmés par le filet à l'approbation.
        setScanCandidates(null);
      }

      setHighlightedLineId(lineId);
      setTimeout(() => {
        lineRefs.current[lineId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      setTimeout(() => setHighlightedLineId(null), 2500);
    } catch (err: any) {
      alert(err.response?.data?.message || `Échec du scan "${barcode}"`);
    }
  };

  // Sélectionner un produit suggéré pour une ligne
  const handleSelectProduct = async (lineId: string, productId: string | null) => {
    if (!tenant?.id || !deliveryId) return;
    const producer = lineProducers[lineId];
    try {
      await api.selectProductForLine(lineId, productId, producer);
      // En passant à "Nouveau", effacer le code-barres s'il avait été pré-rempli
      // automatiquement depuis le produit matché (sinon le nouveau produit traînerait
      // le code-barres d'un autre — ex. betterave crue héritant du jus de betterave).
      if (productId === null) {
        const current = lines.find(l => l.id === lineId);
        if (current?.barcode && current.barcode === current.matched_product_barcode) {
          try {
            await api.updateDeliveryLine(tenant.id, lineId, { barcode: '' });
          } catch { /* ignore */ }
          setLineBarcodes(prev => ({ ...prev, [lineId]: '' }));
        }
      }
      // Suivre l'intention "Nouveau" (null) vs "match explicite" (productId présent)
      setLinesMarkedAsNew(prev => {
        const next = new Set(prev);
        if (productId === null) next.add(lineId);
        else next.delete(lineId);
        return next;
      });
      // Recharger les lignes
      const linesData = await api.getDeliveryLines(tenant.id, deliveryId);
      setLines(linesData);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Échec de la sélection');
    }
  };

  // Sauvegarder le producteur d'une ligne + recharger les suggestions
  const handleSaveLineProducer = async (lineId: string) => {
    const producer = lineProducers[lineId]?.trim();
    if (!producer) return;
    setEditingProducerLineId(null);
    // Mettre à jour le producer_name sur la ligne
    if (tenant?.id) {
      try {
        await api.updateDeliveryLine(tenant.id, lineId, { producer_name: producer });
      } catch { /* ignore */ }
    }
    // Recharger les suggestions avec le producteur
    await loadSuggestions(lineId, producer);
  };

  // Réceptionner une ligne (approuver + créer produit + lot)
  const handleCloseDelivery = async () => {
    if (!tenant?.id || !delivery) return;
    try {
      await api.closeDelivery(tenant.id, delivery.id);
      await loadDeliveryDetails();
      await loadDeliveryList();
    } catch (e: any) {
      alert('❌ ' + (e?.response?.data?.message || e?.message || 'Clôture impossible'));
    }
  };

  // Invitation à annoncer une nouveauté, posée juste après la réception d'une ligne.
  const invitationNouveaute = useInvitationNouveaute();

  const handleApproveLine = async (lineId: string) => {
    if (!tenant?.id || !deliveryId) return;

    // Garde-fou nature (pesé/kg vs pièce) : si l'unité réceptionnée diffère de
    // la fiche produit existante, demander confirmation avant de créer le lot.
    // Évite qu'un produit au poids reçoive un lot "à la pièce" (ou l'inverse).
    const lineForGuard = lines.find(l => l.id === lineId);
    const isExistingMatchGuard = !!lineForGuard?.matched_product_id && !linesMarkedAsNew.has(lineId);
    if (isExistingMatchGuard && lineForGuard?.matched_product_unit != null) {
      const chosenUnit = ((editingLineId === lineId ? editedFields.unit : undefined) ?? lineForGuard.unit ?? '')
        .toString().toLowerCase();
      const chosenWeighted = chosenUnit === 'kg';
      const prodWeighted = lineForGuard.matched_product_is_weighted === true
        || (lineForGuard.matched_product_unit ?? '').toString().toLowerCase() === 'kg';
      if (chosenUnit && chosenWeighted !== prodWeighted) {
        const ok = window.confirm(
          `« ${lineForGuard.matched_product_name || lineForGuard.normalized_name || lineForGuard.raw_name} » est enregistré ` +
          `${prodWeighted ? 'AU POIDS (kg)' : 'À LA PIÈCE'} sur sa fiche.\n` +
          `Vous le réceptionnez ${chosenWeighted ? 'AU POIDS (kg)' : 'À LA PIÈCE'}.\n\n` +
          `Confirmer ce changement de nature ?\n(Annuler pour corriger l'unité de la ligne)`
        );
        if (!ok) return;
      }
    }

    setProcessingLineId(lineId);
    try {
      const updates: any = {};
      const dlcValue = lineDlcValues[lineId];
      if (dlcValue) updates.default_expiry_date = dlcValue;

      // Inclure les champs édités inline
      if (editingLineId === lineId && Object.keys(editedFields).length > 0) {
        Object.assign(updates, editedFields);
        setEditingLineId(null);
        setEditedFields({});
      }

      // Toujours envoyer le prix de vente conseillé (modifiable par l'utilisateur)
      if (lineSuggestedPrices[lineId]) {
        updates.suggested_price_cents = lineSuggestedPrices[lineId];
      }

      // Produit existant : appliquer le nouveau prix de vente au rayon (fiche + POS) si
      //  - bouton « Appliquer le prix proposé » cliqué → choix explicite, PAS de confirmation ;
      //  - sinon prix modifié manuellement + différent du prix actuel → confirmation.
      const line = lines.find(l => l.id === lineId);
      const enteredSale = lineSuggestedPrices[lineId];
      const isExistingMatch = !!line?.matched_product_id && !linesMarkedAsNew.has(lineId);
      if (isExistingMatch && enteredSale) {
        if (linesApplyProposed.has(lineId)) {
          updates.apply_sale_price = true;
        } else if (linesPriceEdited.has(lineId)) {
          const matchedSug = (lineSuggestions[lineId] || []).find(s => s.id === line!.matched_product_id);
          const currentPrice = matchedSug?.price_cents;
          if (currentPrice == null || currentPrice !== enteredSale) {
            const currentLabel = currentPrice != null ? `${(currentPrice / 100).toFixed(2)} €` : 'son prix actuel';
            const ok = window.confirm(
              `Ce produit est actuellement à ${currentLabel}.\n` +
              `Appliquer le nouveau prix de vente ${(enteredSale / 100).toFixed(2)} € au rayon (stock déjà présent + nouveau) ?`
            );
            if (ok) updates.apply_sale_price = true;
          }
        }
      }

      // Si l'utilisateur a cliqué "Nouveau", forcer la création même si un produit existant a le même barcode
      if (linesMarkedAsNew.has(lineId)) {
        updates.force_new_product = true;
      }

      let result = await api.approveLine(tenant.id, lineId, Object.keys(updates).length > 0 ? updates : undefined);

      // FILET ANTI-DOUBLON : le backend refuse de créer un nouveau produit si des
      // similaires existent. On propose à l'opérateur de choisir un produit existant
      // (annuler → scanner/sélectionner) ou de confirmer la création (OK).
      if (result?.requires_confirmation) {
        const names = (result.duplicate_candidates || [])
          .map((c: any) => `• ${c.name}${c.barcode ? ` (EAN ${c.barcode})` : ''}`)
          .join('\n');
        const ok = window.confirm(
          `${result.message}\n\nProduits existants similaires :\n${names}\n\n` +
          `• Annuler = ne RIEN créer → sélectionnez la ligne et scannez le code-barres du bon produit.\n` +
          `• OK = créer quand même un NOUVEAU produit distinct.`
        );
        if (!ok) {
          // L'opérateur va choisir/scanner le bon produit → on laisse la ligne en attente
          setProcessingLineId(null);
          return;
        }
        // Confirmation explicite → forcer la création
        result = await api.approveLine(tenant.id, lineId, { ...updates, force_new_product: true });
      }

      // Recharger les lignes
      const linesData = await api.getDeliveryLines(tenant.id, deliveryId);
      setLines(linesData);
      setSelectedLineId(prev => (prev === lineId ? null : prev));
      setScanCandidates(prev => (prev?.lineId === lineId ? null : prev));

      // Première arrivée ou retour : le demander AVANT de quitter l'onglet (la
      // dernière ligne d'un bon bascule sur la mise en rayon et emporterait
      // l'invitation avec lui).
      if (result?.success) await invitationNouveaute.proposer(result);

      // Vérifier si toutes les lignes sont traitées
      const stillPending = linesData.filter((l: any) => l.status === 'pending');
      if (stillPending.length === 0) {
        onDeliveryApplied();
      }

      if (result?.warning) {
        alert(result.warning);
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Échec de la réception');
    } finally {
      setProcessingLineId(null);
    }
  };

  // Rejeter une ligne
  const handleRejectLine = async (lineId: string, reason?: string) => {
    if (!tenant?.id || !deliveryId) return;
    setProcessingLineId(lineId);
    try {
      await api.rejectLine(tenant.id, lineId, reason);
      const linesData = await api.getDeliveryLines(tenant.id, deliveryId);
      setLines(linesData);
      setRejectingLineId(null);
      setRejectReason('');
      setCustomRejectReason('');

      const stillPending = linesData.filter((l: any) => l.status === 'pending');
      if (stillPending.length === 0) {
        onDeliveryApplied();
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Échec du rejet');
    } finally {
      setProcessingLineId(null);
    }
  };

  const confirmReject = () => {
    if (!rejectingLineId) return;
    const reason = rejectReason === 'Autre' ? customRejectReason : rejectReason;
    handleRejectLine(rejectingLineId, reason || undefined);
  };

  // Rejeter toutes les lignes pending restantes
  const handleRejectAllPending = async () => {
    if (!tenant?.id || !deliveryId) return;
    const pendingLines = lines.filter(l => l.status === 'pending');
    if (pendingLines.length === 0) return;
    if (!window.confirm(`Rejeter les ${pendingLines.length} ligne(s) non réceptionnée(s) comme "Produit manquant" ?`)) return;

    try {
      for (const l of pendingLines) {
        await api.rejectLine(tenant.id, l.id, 'Produit manquant');
      }
      const linesData = await api.getDeliveryLines(tenant.id, deliveryId);
      setLines(linesData);
      onDeliveryApplied();
    } catch (err: any) {
      alert('Erreur lors du rejet en masse');
    }
  };

  // Sauvegarder l'édition inline
  const handleSaveInlineEdit = async (lineId: string) => {
    if (!tenant?.id) return;
    try {
      await api.updateDeliveryLine(tenant.id, lineId, editedFields);
      const linesData = await api.getDeliveryLines(tenant.id, deliveryId!);
      setLines(linesData);
      setEditingLineId(null);
      setEditedFields({});
      // Le nom a pu être renommé (ex. libellé fournisseur → nom catalogue) : recalculer
      // TOUJOURS les propositions sur le nom enregistré (même si un produit est déjà lié).
      // C'est le seul moyen de rafraîchir les vignettes pour les produits SANS code-barres
      // (fruits/légumes), où le matching repose uniquement sur le nom. Le verrou
      // « !matched_product_id » empêchait ce refresh dès qu'un (mauvais) match existait.
      const saved: any = linesData.find((l: any) => l.id === lineId);
      if (saved && saved.status === 'pending') {
        loadSuggestions(lineId, lineProducers[lineId] || saved.producer_name || undefined);
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Échec de la sauvegarde');
    }
  };

  // Ouvrir l'édition inline
  const startInlineEdit = (line: any) => {
    setEditingLineId(line.id);
    setEditedFields({
      normalized_name: line.normalized_name || line.raw_name,
      quantity: line.quantity,
      unit: line.unit,
      unit_price_ttc_cents: line.unit_price_ttc_cents,
      tax_rate: line.tax_rate,
      category_hint: line.category_hint,
      origin_country: line.origin_country,
    });
  };

  // Conditionnement : convertir un colis vendu au poids (ex. sac 5 kg → vente/kg)
  // quantité ×N, prix d'achat ÷N, unité = kg, prix de vente recalculé.
  // Conversion colis → kg. Le poids d'un colis se saisit dans un modal et NON via
  // window.prompt() : Safari neutralise prompt() quand la PWA tourne en mode
  // installé (écran d'accueil). Il renvoyait null sans rien afficher, et la
  // conversion échouait en silence sur les iPad de réception.
  const [condLine, setCondLine] = useState<any | null>(null);
  const [condWeight, setCondWeight] = useState('');

  const handleConditionnement = (line: any) => {
    setCondLine(line);
    setCondWeight('');
  };

  const applyConditionnement = () => {
    const line = condLine;
    if (!line) return;
    const n = parseFloat((condWeight || '').replace(',', '.'));
    if (isNaN(n) || n <= 0) {
      alert('Poids invalide');
      return;
    }
    const curQty = Number(editedFields.quantity ?? line.quantity ?? 1) || 1;
    const curPurchase = Number(editedFields.unit_price_ttc_cents ?? line.unit_price_ttc_cents ?? 0);
    const newQty = Math.round(curQty * n * 1000) / 1000; // au gramme près
    const newPurchase = curPurchase ? Math.round(curPurchase / n) : 0;
    setEditedFields(prev => ({
      ...prev,
      quantity: newQty,
      unit: 'kg',
      unit_price_ttc_cents: newPurchase,
    }));
    if (newPurchase) {
      setLineSuggestedPrices(prev => ({ ...prev, [line.id]: suggestedSalePriceCents(newPurchase) }));
    }
    setCondLine(null);
  };

  // Supprimer la livraison
  const handleDeleteDelivery = async () => {
    if (!deliveryId || !tenant?.id) return;
    if (!window.confirm('Supprimer cette livraison ?')) return;
    try {
      await api.deleteDelivery(tenant.id, deliveryId);
      onDeliveryChange(null);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Échec de la suppression');
    }
  };

  // ========================
  // MODE SÉLECTION
  // ========================
  if (!deliveryId) {
    return (
      <div>
        {loadingList ? (
          <div className="text-center py-8 text-gray-500">Chargement...</div>
        ) : deliveries.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600 text-lg">Aucune livraison en attente</p>
            <p className="text-gray-500 text-sm mt-1">Importez un document dans l'onglet Import</p>
          </div>
        ) : (
          <div className="space-y-2">
            {deliveries.map((d) => (
              <button
                key={d.id}
                onClick={() => onDeliveryChange(d.id)}
                className="w-full text-left bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors min-h-[60px]"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold truncate">{d.supplier || 'Fournisseur inconnu'}</span>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                        d.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {d.status === 'pending' ? 'En attente' : 'En cours'}
                      </span>
                    </div>
                    <div className="flex gap-3 text-xs text-gray-500">
                      <span>{d.document_type}</span>
                      {d.document_number && <span>#{d.document_number}</span>}
                      <span>{new Date(d.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  {d.ai_confidence_score && (
                    <span className={`text-xs font-semibold px-2 py-1 rounded ml-2 ${
                      d.ai_confidence_score >= 90 ? 'bg-green-100 text-green-700' :
                      d.ai_confidence_score >= 70 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {d.ai_confidence_score}%
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ========================
  // MODE VÉRIFICATION
  // ========================
  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-lg text-gray-500">Chargement de la livraison...</div>
      </div>
    );
  }

  if (!delivery) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Livraison introuvable</p>
        <button onClick={() => onDeliveryChange(null)} className="mt-3 text-blue-600 font-medium">
          Retour
        </button>
      </div>
    );
  }

  // Filtre recherche (matche sur tous les noms dispo, incluant le libellé fournisseur)
  const filteredLines = lines.filter(line => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    const haystack = [
      line.matched_product_name,
      line.normalized_name,
      line.raw_name,
      line.barcode,
      line.matched_product_barcode,
      line.product_code,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });

  const pendingLines = filteredLines.filter(l => l.status === 'pending');
  const processedLines = filteredLines.filter(l => l.status !== 'pending');
  const totalPending = lines.filter(l => l.status === 'pending').length;
  const approvedCount = lines.filter(l => l.status !== 'pending' && l.status !== 'rejected').length;
  const rejectedCount = lines.filter(l => l.status === 'rejected').length;
  const totalProcessed = approvedCount + rejectedCount;
  const progressPercent = lines.length > 0 ? Math.round((totalProcessed / lines.length) * 100) : 0;

  const handleBarcodeScan = async (barcode: string) => {
    if (!isOrderDelivery || !deliveryId) return;
    try {
      const result = await api.receiveLineByBarcode(deliveryId, barcode);
      setLines(prev => prev.map(l =>
        l.id === result.id
          ? { ...l, reception_status: 'received' as const, received_quantity: result.received_quantity ?? l.quantity }
          : l
      ));
      setHighlightedLineId(result.id);
      setTimeout(() => lineRefs.current[result.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
      setTimeout(() => setHighlightedLineId(null), 3000);
    } catch {
      const pendingNoBarcode = lines.find(l => l.status === 'pending' && !l.barcode && !l.matched_product_id);
      if (pendingNoBarcode && tenant?.id) {
        setLineBarcodes(prev => ({ ...prev, [pendingNoBarcode.id]: barcode }));
        (async () => {
          try {
            const updated: any = await api.updateDeliveryLine(tenant.id, pendingNoBarcode.id, { barcode });
            setLines(prev => prev.map(l => l.id === pendingNoBarcode.id ? { ...l, ...updated } : l));
            if (updated?.matched_product_id) {
              setLinesMarkedAsNew(prev => {
                const next = new Set(prev);
                next.delete(pendingNoBarcode.id);
                return next;
              });
              loadSuggestions(pendingNoBarcode.id, lineProducers[pendingNoBarcode.id] || undefined);
            }
          } catch { /* ignore */ }
        })();
        setHighlightedLineId(pendingNoBarcode.id);
        setTimeout(() => lineRefs.current[pendingNoBarcode.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
        setTimeout(() => setHighlightedLineId(null), 3000);
      } else {
        alert(`Code "${barcode}" non trouve dans cette commande`);
      }
    }
  };

  // Signale le prix qui sera RÉELLEMENT appliqué à la fiche produit à l'approbation :
  // - nouveau produit → le prix « Vente » saisi crée la fiche ;
  // - produit existant + prix « Vente » modifié à la main → ce prix sera proposé au rayon ;
  // - produit existant NON modifié → le prix actuel de la fiche est CONSERVÉ (le « Vente »
  //   proposé, auto = achat × 1,5, reste indicatif et n'est PAS appliqué).
  const renderAppliedPriceBadge = (line: DeliveryLine) => {
    const isExistingMatch = !!line.matched_product_id && !linesMarkedAsNew.has(line.id);
    if (!isExistingMatch) {
      return (
        <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-800 font-bold">
          ✓ prix appliqué à la fiche
        </span>
      );
    }
    const matchedSug = (lineSuggestions[line.id] || []).find((s) => s.id === line.matched_product_id);
    const histCents = matchedSug?.price_cents;
    const proposedCents = lineSuggestedPrices[line.id];
    const willApply = linesApplyProposed.has(line.id) || linesPriceEdited.has(line.id);
    if (willApply) {
      return (
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-800 font-bold">
            ✓ ce prix sera appliqué
          </span>
          <button
            type="button"
            onClick={() => {
              setLinesApplyProposed((prev) => { const n = new Set(prev); n.delete(line.id); return n; });
              setLinesPriceEdited((prev) => { const n = new Set(prev); n.delete(line.id); return n; });
            }}
            className="text-[10px] text-gray-500 underline hover:text-gray-700"
          >
            annuler
          </button>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 flex-wrap">
        <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
          proposé,&nbsp;<b>non appliqué</b>
          {histCents != null ? ` — prix conservé : ${(histCents / 100).toFixed(2)} €` : ''}
        </span>
        {proposedCents != null && proposedCents > 0 && proposedCents !== histCents && (
          <button
            type="button"
            onClick={() => setLinesApplyProposed((prev) => new Set(prev).add(line.id))}
            className="text-[10px] px-2 py-0.5 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700"
          >
            Appliquer le prix proposé ({(proposedCents / 100).toFixed(2)} €)
          </button>
        )}
      </span>
    );
  };

  return (
    <div>
      {invitationNouveaute.element}
      {articleEnPlusOuvert && deliveryId && (
        <ArticleEnPlusModal
          deliveryId={deliveryId}
          onClose={() => setArticleEnPlusOuvert(false)}
          onAjoute={() => loadDeliveryDetails()}
        />
      )}
      {/* En-tête livraison + barre de progression */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <button
              onClick={() => onDeliveryChange(null)}
              className="text-sm text-blue-600 font-medium mb-1"
            >
              ← Retour
            </button>
            <div className="flex items-center gap-2 flex-wrap">
              {isEditingSupplier ? (
                <div className="flex flex-col gap-1">
                  <div className="flex gap-1 items-center">
                  <input
                    type="text"
                    list="verif-suppliers"
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    placeholder="Choisir ou saisir…"
                    className="px-2 py-1 border border-gray-300 rounded text-base font-bold"
                    autoFocus
                  />
                  <datalist id="verif-suppliers">
                    {suppliers.map(s => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                  <button onClick={handleSaveSupplier} className="text-green-600 px-1 min-h-[48px]">OK</button>
                  <button onClick={() => { setIsEditingSupplier(false); setSupplierName(delivery.supplier || ''); }} className="text-red-600 px-1 min-h-[48px]">X</button>
                  </div>
                  {supplierSuggestion.suggestion && !supplierSuggestion.exact && (
                    <button
                      type="button"
                      onClick={() => setSupplierName(supplierSuggestion.suggestion as string)}
                      className="self-start inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs hover:bg-amber-100"
                    >
                      💡 Proche de «&nbsp;<strong>{supplierSuggestion.suggestion}</strong>&nbsp;» — utiliser
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-0.5">
                  <h3 className="text-lg font-bold" onClick={() => setIsEditingSupplier(true)}>
                    {delivery.supplier || 'Fournisseur inconnu'}
                  </h3>
                  {deliverySupplierSuggestion.suggestion && !deliverySupplierSuggestion.exact && (
                    <button
                      type="button"
                      onClick={() => { setSupplierName(deliverySupplierSuggestion.suggestion as string); setIsEditingSupplier(true); }}
                      className="self-start inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs hover:bg-amber-100"
                    >
                      💡 Proche de «&nbsp;<strong>{deliverySupplierSuggestion.suggestion}</strong>&nbsp;» — corriger
                    </button>
                  )}
                </div>
              )}
              <span className="text-sm text-gray-500">
                {delivery.document_type} {delivery.document_number && `#${delivery.document_number}`}
              </span>
            </div>
          </div>
          <div className="flex gap-1">
            {/* Bouton Modifier fournisseur */}
            {isOrderDelivery && (
              <button
                onClick={() => setIsEditingSupplier(true)}
                className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium min-h-[48px] flex items-center"
              >
                Fournisseur
              </button>
            )}
            {/* Bouton Joindre BL (ORDER) ou voir Doc (BL/INVOICE) */}
            {isOrderDelivery && !delivery.document_url && (
              <>
                <input
                  ref={blFileInputRef}
                  type="file"
                  accept=".pdf,application/pdf,image/*"
                  className="hidden"
                  onChange={handleAttachBL}
                />
                <button
                  onClick={() => blFileInputRef.current?.click()}
                  disabled={attachingBL}
                  className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg text-xs font-medium min-h-[48px] flex items-center"
                >
                  {attachingBL ? '...' : 'Joindre BL'}
                </button>
              </>
            )}
            {delivery.document_url && (
              <a
                href={delivery.document_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-xs font-medium min-h-[48px] flex items-center"
              >
                BL
              </a>
            )}
            {delivery.status !== 'applied' && (
              <button
                onClick={() => setArticleEnPlusOuvert(true)}
                className="px-3 py-2 bg-emerald-100 text-emerald-800 rounded-lg text-xs font-medium min-h-[48px]"
                title="Un article livré qui n'est pas dans le document importé (reliquat, ajout du fournisseur…)"
              >
                + Article en plus
              </button>
            )}
            {delivery.status !== 'applied' && (
              <button
                onClick={handleDeleteDelivery}
                className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-xs font-medium min-h-[48px]"
              >
                Suppr.
              </button>
            )}
            {delivery.status !== 'applied' && totalPending === 0 && lines.length > 0 && (
              <button
                onClick={handleCloseDelivery}
                className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs font-medium min-h-[48px]"
                title="Marquer la livraison comme terminée (toutes les lignes sont traitées)"
              >
                ✓ Clôturer
              </button>
            )}
          </div>
        </div>

        {/* Barre de progression */}
        <div className="mb-2">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-600">
              {totalProcessed}/{lines.length} traitees
            </span>
            <div className="flex gap-3 text-xs">
              {approvedCount > 0 && <span className="text-green-700">{approvedCount} recu</span>}
              {rejectedCount > 0 && <span className="text-red-700">{rejectedCount} refus</span>}
              {totalPending > 0 && <span className="text-yellow-700">{totalPending} en attente</span>}
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all ${progressPercent === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Recherche */}
      {lines.length > 5 && (
        <div className="mb-3 flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              // Détecter un scan barcode (Enter + valeur numérique >= 5 chars)
              if (e.key === 'Enter' && /^\d{5,}$/.test(searchQuery.trim())) {
                e.preventDefault();
                const barcode = searchQuery.trim();
                setSearchQuery('');
                handleBarcodeScan(barcode);
              }
            }}
            placeholder="Rechercher par nom, code-barres, code..."
            className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm min-h-[48px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="button"
            onClick={() => setScannerOpen(true)}
            className="px-4 flex items-center justify-center bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 min-h-[48px]"
            aria-label="Scanner via caméra"
            title="Scanner via caméra"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* EN ATTENTE — cartes détaillées               */}
      {/* ═══════════════════════════════════════════ */}
      {pendingLines.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              En attente ({pendingLines.length})
            </h4>
            {pendingLines.length > 1 && approvedCount > 0 && (
              <button
                onClick={handleRejectAllPending}
                className="px-3 py-1.5 bg-orange-100 text-orange-800 text-xs rounded-lg font-medium min-h-[48px]"
              >
                Tout refuser (manquant)
              </button>
            )}
          </div>

          <div className="space-y-3">
            {pendingLines.map((line) => {
              const isHighlighted = highlightedLineId === line.id;
              const isProcessing = processingLineId === line.id;
              const isEditing = editingLineId === line.id;
              const isSelected = selectedLineId === line.id;

              return (
                <div
                  key={line.id}
                  ref={(el) => { lineRefs.current[line.id] = el; }}
                  onClick={() => setSelectedLineId(line.id)}
                  className={`bg-white border-2 rounded-xl p-4 transition-all cursor-pointer ${
                    isHighlighted ? 'border-blue-500 ring-2 ring-blue-300 animate-pulse' :
                    isSelected ? 'border-emerald-500 ring-2 ring-emerald-300' :
                    'border-gray-200'
                  } ${isProcessing ? 'opacity-60' : ''}`}
                >
                  {/* Bandeau "ligne sélectionnée → scannez l'article" */}
                  {isSelected && !(scanCandidates?.lineId === line.id) && (
                    <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-xs font-medium">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
                      <span>Ligne sélectionnée — scannez le code-barres de l'article</span>
                    </div>
                  )}

                  {/* EAN partagé entre plusieurs produits (ex. café moulu/grain) :
                      choix manuel obligatoire — aucune liaison automatique */}
                  {scanCandidates?.lineId === line.id && (
                    <div
                      className="mb-3 p-3 bg-amber-50 border-2 border-amber-300 rounded-lg"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="text-xs font-semibold text-amber-800 mb-2">
                        ⚠️ Ce code-barres est utilisé par {scanCandidates.candidates.length} produits différents. Lequel recevez-vous ?
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {scanCandidates.candidates.map((c) => (
                          <button
                            key={c.id}
                            onClick={async () => {
                              await handleSelectProduct(line.id, c.id);
                              setScanCandidates(null);
                            }}
                            className="flex items-center justify-between px-3 py-2 bg-white border border-amber-300 rounded-lg text-sm hover:bg-amber-100 text-left"
                          >
                            <span className="font-medium">{c.name}</span>
                            {c.active === false && <span className="text-[10px] text-gray-400">inactif</span>}
                          </button>
                        ))}
                        <button
                          onClick={() => setScanCandidates(null)}
                          className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                        >
                          Aucun — corriger manuellement
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Nom + Photo + Quantité en gros */}
                  <div className="flex items-start justify-between mb-2">
                    {/* Photo produit catalogue */}
                    {line.matched_product_image_url && (
                      <div className="w-14 h-14 flex-shrink-0 mr-3 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                        <img
                          src={line.matched_product_image_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <ProductAutocomplete
                          value={editedFields.normalized_name || ''}
                          onChange={(v) => setEditedFields({ ...editedFields, normalized_name: v })}
                          onProductSelect={(p: ProductSuggestion) => {
                            setEditedFields({
                              ...editedFields,
                              normalized_name: p.name,
                              barcode: p.barcode || editedFields.barcode,
                              product_code: p.code || editedFields.product_code,
                              category_hint: p.category_id || editedFields.category_hint,
                              matched_product_id: p.id,
                            });
                          }}
                          placeholder="Nom du produit..."
                        />
                      ) : (
                        <h3 className="font-bold text-base leading-tight">
                          {line.matched_product_name || line.normalized_name || line.raw_name}
                        </h3>
                      )}
                      {line.matched_product_name && (
                        <span className="text-[10px] text-green-600">produit catalogue</span>
                      )}
                      {line.matched_product_barcode && (
                        <span className="text-[10px] text-gray-400 ml-2">EAN {line.matched_product_barcode}</span>
                      )}
                      {/* Sortie de secours : le match est faux → réinitialiser pour
                          re-scanner / re-choisir le bon produit (ré-affiche EAN + suggestions) */}
                      {!isEditing && line.matched_product_id && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSelectProduct(line.id, null); }}
                          className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 ml-2 font-medium hover:bg-amber-100"
                          title="Mauvais produit ? Réinitialiser pour re-scanner ou choisir"
                        >
                          ✏️ changer de produit
                        </button>
                      )}
                      {/* Libellé fournisseur (nom original parsé depuis l'email/BL) — toujours visible si différent du titre */}
                      {line.raw_name && line.raw_name.trim().toLowerCase() !== (line.matched_product_name || line.normalized_name || '').trim().toLowerCase() && (
                        <div className="mt-1 text-xs text-gray-600 flex items-start gap-1">
                          <span className="text-gray-400 flex-shrink-0">📦 Libellé fournisseur :</span>
                          <span className="italic">{line.raw_name}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            step="0.01"
                            value={editedFields.quantity || ''}
                            onChange={(e) => setEditedFields({ ...editedFields, quantity: parseFloat(e.target.value) || 0 })}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-lg font-bold"
                          />
                          <select
                            value={editedFields.unit || 'pièce'}
                            onChange={(e) => setEditedFields({ ...editedFields, unit: e.target.value })}
                            className={`px-2 py-1 border-2 rounded text-sm font-bold ${
                              editedFields.unit === 'kg'
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : editedFields.unit === 'L'
                                ? 'border-orange-500 bg-orange-50 text-orange-700'
                                : 'border-green-500 bg-green-50 text-green-700'
                            }`}
                          >
                            <option value="kg">⚖️ kg</option>
                            <option value="pièce">🔢 pièce</option>
                            <option value="L">💧 L</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => handleConditionnement(line)}
                            title="Conditionnement : convertir un colis vendu au poids (quantité ×, prix d'achat ÷, kg)"
                            className="px-2 py-1 border-2 border-purple-400 bg-purple-50 text-purple-700 rounded text-xs font-bold whitespace-nowrap"
                          >
                            📦→⚖️
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-end gap-1">
                          <div className="text-xl font-bold text-blue-700">{line.quantity}</div>
                          {(() => {
                            const u = (line.unit || 'pièce').toLowerCase();
                            const isKg = u === 'kg';
                            const isL = u === 'l';
                            return (
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${
                                  isKg
                                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                    : isL
                                    ? 'bg-orange-100 text-orange-700 border border-orange-300'
                                    : 'bg-green-100 text-green-700 border border-green-300'
                                }`}
                                title="Mode de vente — tap Editer pour modifier"
                              >
                                {isKg ? '⚖️ Au poids' : isL ? '💧 Litre' : '🔢 À la pièce'}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Producteur + Suggestions produit */}
                  {!isEditing && (
                    <div className="mb-3 pb-2 border-b border-gray-100">
                      {/* Producteur par ligne */}
                      <div className="flex items-center gap-2 mb-2">
                        {editingProducerLineId === line.id ? (
                          <div className="flex items-center gap-1 flex-1">
                            <span className="text-xs text-gray-500">Producteur:</span>
                            <input
                              type="text"
                              value={lineProducers[line.id] || ''}
                              onChange={(e) => setLineProducers(prev => ({ ...prev, [line.id]: e.target.value }))}
                              className="px-2 py-1 border border-purple-300 rounded text-xs flex-1"
                              placeholder="Nom du producteur..."
                              autoFocus
                              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveLineProducer(line.id); }}
                            />
                            <button
                              onClick={() => handleSaveLineProducer(line.id)}
                              className="text-green-600 px-1 text-xs min-h-[28px] font-medium"
                            >
                              OK
                            </button>
                            <button
                              onClick={() => setEditingProducerLineId(null)}
                              className="text-gray-400 px-1 text-xs min-h-[28px]"
                            >
                              X
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingProducerLineId(line.id);
                              if (!lineProducers[line.id] && line.producer_name) {
                                setLineProducers(prev => ({ ...prev, [line.id]: line.producer_name! }));
                              }
                            }}
                            className={`px-2 py-1 rounded text-xs font-medium min-h-[28px] ${
                              lineProducers[line.id] || line.producer_name
                                ? 'bg-purple-50 text-purple-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {lineProducers[line.id] || line.producer_name
                              ? `Prod: ${lineProducers[line.id] || line.producer_name}`
                              : '+ Producteur'}
                          </button>
                        )}
                      </div>

                      {/* 3 suggestions produit + Nouveau produit */}
                      <div className="grid grid-cols-4 gap-2">
                        {loadingSuggestionsFor.has(line.id) ? (
                          <span className="col-span-4 text-xs text-gray-400 animate-pulse py-4 text-center">Recherche produits...</span>
                        ) : (lineSuggestions[line.id] || []).length > 0 ? (
                          <>
                            {(lineSuggestions[line.id] || []).slice(0, 3).map((sug) => (
                              <button
                                key={sug.id}
                                onClick={() => handleSelectProduct(line.id, sug.id)}
                                className={`flex flex-col items-center rounded-xl border-2 p-1.5 transition-colors min-h-[100px] ${
                                  line.matched_product_id === sug.id
                                    ? 'border-green-500 bg-green-50 ring-1 ring-green-300'
                                    : 'border-gray-200 bg-white hover:bg-gray-50 active:bg-gray-100'
                                }`}
                              >
                                <div
                                  className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 mb-1 flex-shrink-0"
                                  onClick={(e) => {
                                    if (sug.image_url) {
                                      e.stopPropagation();
                                      setZoomedImage(sug.image_url);
                                    }
                                  }}
                                >
                                  {sug.image_url ? (
                                    <img src={sug.image_url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-300 text-lg">?</div>
                                  )}
                                </div>
                                <span className="text-[10px] leading-tight text-center font-medium line-clamp-2">{sug.name}</span>
                                {sug.price_cents != null && (
                                  <span className="text-[10px] text-blue-600 font-semibold">{(sug.price_cents / 100).toFixed(2)}€</span>
                                )}
                                {sug.stock_quantity != null && sug.stock_quantity > 0 && (
                                  <span className="text-[9px] text-gray-400">stock: {sug.stock_quantity}</span>
                                )}
                              </button>
                            ))}
                          </>
                        ) : (
                          <span className="col-span-3 text-xs text-gray-400 py-4 text-center">Aucun match catalogue</span>
                        )}
                        {/* Bouton Nouveau produit */}
                        <button
                          onClick={() => handleSelectProduct(line.id, null)}
                          className={`flex flex-col items-center justify-center rounded-xl border-2 p-1.5 min-h-[90px] transition-colors ${
                            !line.matched_product_id
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-dashed border-gray-300 text-gray-400 hover:bg-gray-50'
                          }`}
                        >
                          <span className="text-2xl mb-1">+</span>
                          <span className="text-[10px] font-medium">Nouveau</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Code-barres (visible si absent — essentiel pour les nouveaux produits) */}
                  {!isEditing && !line.matched_product_id && (
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                      <span className="text-xs text-gray-500">EAN:</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        data-scan-target
                        value={lineBarcodes[line.id] ?? line.barcode ?? ''}
                        onChange={(e) => setLineBarcodes(prev => ({ ...prev, [line.id]: e.target.value }))}
                        placeholder="Scannez ou saisissez le code-barres puis Entrée..."
                        className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm min-h-[48px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        onKeyDown={(e) => {
                          // Validation au clavier (Entrée) — pas seulement au blur
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const bc = (lineBarcodes[line.id] ?? '').trim();
                            if (bc) applyScanToLine(line.id, bc);
                          }
                        }}
                        onBlur={() => {
                          const bc = (lineBarcodes[line.id] ?? '').trim();
                          // Saisie manuelle = même moteur que le scan : retrouve le produit
                          // existant par EAN (prioritaire) et annule l'intention "Nouveau".
                          if (bc && bc !== (line.barcode ?? '')) applyScanToLine(line.id, bc);
                        }}
                      />
                    </div>
                  )}

                  {/* Détails secondaires */}
                  <div className="flex gap-3 text-xs text-gray-500 flex-wrap mb-3">
                    {isEditing ? (
                      <>
                        <PriceInput
                          label="Achat:"
                          valueCents={editedFields.unit_price_ttc_cents || 0}
                          onChangeCents={(cents) => setEditedFields({ ...editedFields, unit_price_ttc_cents: cents })}
                          compact
                          className="w-36"
                        />
                        <PriceInput
                          label="Vente:"
                          valueCents={lineSuggestedPrices[line.id] || 0}
                          onChangeCents={(cents) => {
                            setLineSuggestedPrices(prev => ({ ...prev, [line.id]: cents }));
                            setLinesPriceEdited(prev => new Set(prev).add(line.id));
                          }}
                          variant="green"
                          compact
                          className="w-36"
                        />
                        {renderAppliedPriceBadge(line)}
                        <div className="flex items-center gap-1">
                          <span>TVA:</span>
                          <select
                            value={editedFields.tax_rate ?? ''}
                            onChange={(e) => setEditedFields({ ...editedFields, tax_rate: parseFloat(e.target.value) })}
                            className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                          >
                            <option value="0">0%</option>
                            <option value="6">6%</option>
                            <option value="12">12%</option>
                            <option value="21">21%</option>
                          </select>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>Cat:</span>
                          <select
                            value={
                              editedFields.category_hint
                                ? (isUuid(editedFields.category_hint)
                                    ? editedFields.category_hint
                                    : (categories.find(c => c.name.toLowerCase() === editedFields.category_hint!.toLowerCase())?.id ?? ''))
                                : ''
                            }
                            onChange={(e) => setEditedFields({ ...editedFields, category_hint: e.target.value })}
                            className="px-1 py-0.5 border border-gray-300 rounded text-xs max-w-[120px]"
                          >
                            <option value="">--</option>
                            {categories.map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>Origine:</span>
                          <input
                            type="text"
                            value={editedFields.origin_country || ''}
                            onChange={(e) => setEditedFields({ ...editedFields, origin_country: e.target.value })}
                            className="w-20 px-1 py-0.5 border border-gray-300 rounded text-xs"
                            placeholder="BE, FR..."
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        {line.unit_price_ttc_cents != null && (
                          <span className="font-medium text-gray-700">
                            Achat {(line.unit_price_ttc_cents / 100).toFixed(2)}€/{line.unit || 'pc'}
                          </span>
                        )}
                        {line.unit_price_ttc_cents != null && (
                          <>
                            <PriceInput
                              label="Vente"
                              valueCents={lineSuggestedPrices[line.id] || 0}
                              onChangeCents={(cents) => {
                                setLineSuggestedPrices(prev => ({ ...prev, [line.id]: cents || 0 }));
                                setLinesPriceEdited(prev => new Set(prev).add(line.id));
                              }}
                              variant="green"
                              compact
                              className="w-36"
                            />
                            {renderAppliedPriceBadge(line)}
                          </>
                        )}
                        {line.tax_rate != null && <span>TVA {line.tax_rate}%</span>}
                        {(line.matched_category_name || resolveCategoryLabel(line.category_hint)) && (
                          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">
                            {line.matched_category_name || resolveCategoryLabel(line.category_hint)}
                          </span>
                        )}
                        {line.origin_country && <span>{line.origin_country}</span>}
                        {line.barcode && <span className="text-gray-400">EAN: {line.barcode}</span>}
                      </>
                    )}
                  </div>

                  {/* Réception ORDER : statut + quantité reçue + photo étiquette */}
                  {isOrderDelivery && (
                    <div className="flex items-center gap-2 flex-wrap mb-2 pb-2 border-b border-gray-100">
                      {/* Badge statut réception */}
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${
                        line.reception_status === 'received' ? 'bg-green-100 text-green-800' :
                        line.reception_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                        line.reception_status === 'missing' ? 'bg-red-100 text-red-800' :
                        line.reception_status === 'extra' ? 'bg-emerald-100 text-emerald-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {line.reception_status === 'received' ? 'Recu' :
                         line.reception_status === 'partial' ? 'Partiel' :
                         line.reception_status === 'missing' ? 'Manquant' :
                         line.reception_status === 'extra' ? 'En plus' :
                         'Attendu'}
                      </span>

                      {/* Quantité commandée vs reçue */}
                      {line.ordered_quantity != null && (
                        <span className="text-xs text-gray-500">
                          Commande: {line.ordered_quantity} {line.unit || 'pc'}
                        </span>
                      )}

                      {/* Bouton réceptionner manuellement */}
                      {line.reception_status !== 'received' && (
                        <button
                          onClick={() => handleReceiveLine(line.id, line.ordered_quantity || line.quantity || 0)}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded font-medium min-h-[48px]"
                        >
                          Marquer recu
                        </button>
                      )}

                    </div>
                  )}

                  {/* Photo étiquette + DLC automatique — proéminent (toutes livraisons : ORDER, BL, facture) */}
                  {(
                    <div className="mb-2 pb-2 border-b border-gray-100">
                      {line.label_photo_url ? (
                        <div className="flex items-center gap-3">
                          <a href={line.label_photo_url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                            <img
                              src={line.label_photo_url}
                              alt="étiquette"
                              className="w-14 h-14 object-cover rounded-lg border border-green-300"
                            />
                          </a>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-green-700">✓ Étiquette photographiée</div>
                            {extractingLabelLineId === line.id ? (
                              <div className="text-xs text-gray-500 mt-0.5">🔍 Lecture de la DLC…</div>
                            ) : labelExtract[line.id] ? (
                              <div className="text-xs text-gray-600 mt-0.5 space-y-0.5">
                                {labelExtract[line.id].dlc ? (
                                  <div>
                                    📅 DLC lue : <b>{(labelExtract[line.id].dlc as string).split('-').reverse().join('/')}</b>{' '}
                                    <span className="text-amber-600">(à vérifier)</span>
                                  </div>
                                ) : (
                                  <div className="text-gray-400">DLC non détectée — saisir à la main</div>
                                )}
                                {labelExtract[line.id].lot_number && (
                                  <div>🏷️ Lot : <b>{labelExtract[line.id].lot_number}</b></div>
                                )}
                              </div>
                            ) : null}
                          </div>
                          <button
                            onClick={() => setWebcamLineId(line.id)}
                            className="shrink-0 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded min-h-[48px]"
                          >
                            Reprendre
                          </button>
                        </div>
                      ) : uploadingPhotoLineId === line.id || extractingLabelLineId === line.id ? (
                        <div className="text-sm text-gray-500 py-2">
                          {uploadingPhotoLineId === line.id ? '📤 Envoi de la photo…' : '🔍 Lecture de la DLC…'}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {/* Primaire : caméra en direct (getUserMedia) — webcam USB du POS OU caméra du smartphone */}
                          <button
                            onClick={() => setWebcamLineId(line.id)}
                            className="flex-1 px-3 py-2.5 text-sm bg-amber-500 text-white rounded-lg font-semibold min-h-[48px] flex items-center justify-center gap-2"
                          >
                            📸 Photo étiquette + DLC
                          </button>
                          {/* Secours : fichier / pellicule */}
                          <button
                            onClick={() => { setLabelPhotoTargetLineId(line.id); labelPhotoInputRef.current?.click(); }}
                            className="px-3 py-2.5 text-sm bg-amber-100 text-amber-700 rounded-lg font-medium min-h-[48px]"
                          >
                            Fichier
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* DLC + Actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-gray-600">DLC:</span>
                      <DLCInput
                        ref={(el) => { dlcRefs.current[line.id] = el; }}
                        value={lineDlcValues[line.id] || ''}
                        onChange={(isoDate) => setLineDlcValues(prev => ({ ...prev, [line.id]: isoDate }))}
                        compact
                        className="w-44"
                      />
                      {labelExtract[line.id]?.dlc && lineDlcValues[line.id] === labelExtract[line.id].dlc && (
                        <span className="text-xs text-amber-600" title="Pré-remplie depuis la photo — vérifier">📷</span>
                      )}
                    </div>

                    <div className="flex gap-2 ml-auto">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => { setEditingLineId(null); setEditedFields({}); }}
                            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm min-h-[48px]"
                          >
                            Annuler
                          </button>
                          <button
                            onClick={() => handleSaveInlineEdit(line.id)}
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold min-h-[48px]"
                          >
                            OK
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startInlineEdit(line)}
                            className="px-3 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm min-h-[48px]"
                            disabled={isProcessing}
                          >
                            Editer
                          </button>
                          <button
                            onClick={() => { setRejectingLineId(line.id); setRejectReason(''); setCustomRejectReason(''); }}
                            className="px-3 py-2.5 bg-red-100 text-red-700 rounded-lg text-sm font-semibold min-h-[48px]"
                            disabled={isProcessing}
                          >
                            Refuser
                          </button>
                          <button
                            onClick={() => handleApproveLine(line.id)}
                            className="px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold min-h-[48px] active:bg-green-700"
                            disabled={isProcessing}
                          >
                            {isProcessing ? '...' : 'Receptionner'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Message de complétion */}
      {pendingLines.length === 0 && lines.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-center">
          <p className="text-green-800 font-semibold text-lg">Livraison traitee</p>
          <p className="text-green-600 text-sm mt-1">
            {approvedCount} produit(s) receptionne(s), {rejectedCount} refuse(s)
          </p>
          <p className="text-green-600 text-sm">Les lots sont disponibles dans l'onglet Mise en Rayon</p>
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* TRAITÉ — liste compacte repliable            */}
      {/* ═══════════════════════════════════════════ */}
      {processedLines.length > 0 && (
        <div>
          <button
            onClick={() => setShowProcessed(!showProcessed)}
            className="w-full flex items-center justify-between text-sm font-semibold text-gray-600 uppercase tracking-wide py-2"
          >
            <span>Traite ({processedLines.length})</span>
            <span>{showProcessed ? '▲' : '▼'}</span>
          </button>

          {showProcessed && (
            <div className="space-y-1">
              {processedLines.map((line) => (
                <div
                  key={line.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                    line.status === 'rejected' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-800'
                  }`}
                >
                  <span className="flex-shrink-0">
                    {line.status === 'rejected' ? 'X' : 'OK'}
                  </span>
                  <span className="flex-1 min-w-0 truncate font-medium">
                    {line.matched_product_name || line.normalized_name || line.raw_name}
                  </span>
                  <span className="flex-shrink-0 text-xs">
                    {line.quantity} {line.unit || 'pc'}
                  </span>
                  {line.unit_price_ttc_cents != null && (
                    <span className="flex-shrink-0 text-xs">
                      {(line.unit_price_ttc_cents / 100).toFixed(2)}€
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal de rejet */}
      {rejectingLineId && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold">Raison du refus</h3>
              <p className="text-sm text-gray-500">
                {lines.find(l => l.id === rejectingLineId)?.normalized_name || lines.find(l => l.id === rejectingLineId)?.raw_name}
              </p>
            </div>
            <div className="p-4 space-y-2">
              {REJECT_REASONS.map((reason) => (
                <button
                  key={reason}
                  onClick={() => setRejectReason(reason)}
                  className={`w-full text-left px-4 py-3 rounded-lg border text-sm min-h-[48px] ${
                    rejectReason === reason ? 'border-red-500 bg-red-50 text-red-800 font-semibold' : 'border-gray-200'
                  }`}
                >
                  {reason}
                </button>
              ))}
              <button
                onClick={() => setRejectReason('Autre')}
                className={`w-full text-left px-4 py-3 rounded-lg border text-sm min-h-[48px] ${
                  rejectReason === 'Autre' ? 'border-red-500 bg-red-50 text-red-800 font-semibold' : 'border-gray-200'
                }`}
              >
                Autre...
              </button>
              {rejectReason === 'Autre' && (
                <input
                  type="text"
                  value={customRejectReason}
                  onChange={(e) => setCustomRejectReason(e.target.value)}
                  placeholder="Precisez la raison..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm min-h-[48px]"
                  autoFocus
                />
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex gap-2">
              <button
                onClick={() => { setRejectingLineId(null); setRejectReason(''); }}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium min-h-[48px]"
              >
                Annuler
              </button>
              <button
                onClick={confirmReject}
                disabled={!rejectReason || (rejectReason === 'Autre' && !customRejectReason.trim())}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg font-semibold min-h-[48px] disabled:bg-gray-300"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sélecteur de ligne : EAN scanné inconnu de la livraison */}
      {scanLineChooser && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold">Quel article venez-vous de scanner ?</h3>
              <p className="text-sm text-gray-500">
                Code <span className="font-mono">{scanLineChooser.barcode}</span> inconnu du catalogue —
                touchez la ligne correspondante pour lui associer ce code-barres.
              </p>
            </div>
            <div className="p-4 space-y-2">
              {lines
                .filter(l => l.status === 'pending')
                .sort((a, b) =>
                  Number(!!(a.barcode || a.matched_product_barcode)) -
                  Number(!!(b.barcode || b.matched_product_barcode))
                )
                .map(l => {
                  const hasEan = !!(l.barcode || l.matched_product_barcode);
                  return (
                    <button
                      key={l.id}
                      onClick={() => {
                        const bc = scanLineChooser.barcode;
                        setScanLineChooser(null);
                        applyScanToLine(l.id, bc);
                      }}
                      className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 text-sm min-h-[48px] flex items-center justify-between gap-2 hover:border-blue-400 hover:bg-blue-50"
                    >
                      <span className="font-medium truncate">{l.normalized_name || l.raw_name}</span>
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        {l.quantity} {l.unit || 'pc'}{hasEan ? ' · EAN déjà lié' : ''}
                      </span>
                    </button>
                  );
                })}
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => setScanLineChooser(null)}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-medium min-h-[48px]"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input pour photo étiquette */}
      <input
        ref={labelPhotoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleLabelPhotoUpload}
      />

      {/* Modal webcam pour photo étiquette */}
      <WebcamCaptureModal
        isOpen={!!webcamLineId}
        onClose={() => setWebcamLineId(null)}
        onCapture={handleWebcamCapture}
      />

      {/* Zoom image produit */}
      {zoomedImage && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setZoomedImage(null)}
        >
          <img
            src={zoomedImage}
            alt=""
            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
          />
        </div>
      )}

      <BarcodeScannerModal
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={(code) => handleBarcodeScan(code)}
        title="Scanner un produit à recevoir"
      />

      {/* Conversion colis → kg (remplace window.prompt, neutralisé en PWA installée) */}
      {condLine && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5">
            <h3 className="text-lg font-bold text-gray-900">Convertir en vente au poids</h3>
            <p className="mt-1 text-sm text-gray-600">
              Poids d'<strong>un</strong> colis, en kg. Exemple : un sac de 5 kg vendu au vrac → saisir 5.
            </p>
            <p className="mt-2 text-xs text-gray-500">
              La quantité sera multipliée, le prix d'achat divisé, et l'unité passée en kg.
            </p>
            <input
              type="text"
              inputMode="decimal"
              autoFocus
              value={condWeight}
              onChange={(e) => setCondWeight(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyConditionnement(); }}
              placeholder="5"
              className="mt-4 w-full px-3 py-3 border border-gray-300 rounded-lg text-lg text-center"
            />
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => setCondLine(null)}
                className="flex-1 py-2.5 rounded-lg bg-gray-100 text-gray-700 font-medium"
              >
                Annuler
              </button>
              <button
                onClick={applyConditionnement}
                disabled={!condWeight.trim()}
                className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50"
              >
                Convertir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
